import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { APIResource } from "../../constants/application";
import { getUrl } from "utils/getURL";
import axiosInstance from "utils/axiosInstance";
import { PAN_STATUS_VARIANTS } from "constants/panStatusVariants";
import { AppSpecificName } from "constants/headerImage";
import { resolveKycErrorMessage } from "utils/kycErrorMessage";

interface IValidatePAN {
  data: {
    mobileNumber: string;
    customerId: string;
    panNumber: string;
    establishmentType: string | null;
    customerSource: string;
    uploadedDocumentKey?: string;
  };
}

interface IPanResponse {
  panNumber?: string;
  fullName?: string;
  panValidityStatus?: string;
  panOperationalStatus?: string;
  isCompliant?: boolean;
  panAadhaarLinkStatus?: string;
  associatedCustomerDataList?: AssociatedCustomerDataList[];
}

export interface AssociatedCustomerDataList {
  customerId: string;
  shopName: string;
}

interface IPANSlice {
  loading: boolean;
  error: string;
  success: boolean;
  isPanInactive: boolean;
  isInitiallyValidated: boolean;
  isPanVerified: boolean;
  isPanUnavailable: boolean;
  panStatus: (typeof PAN_STATUS_VARIANTS)[keyof typeof PAN_STATUS_VARIANTS];
  fullName: string;
  panResponse: null | IPanResponse;
}

export interface IValidatePANResult {
  panStatus: (typeof PAN_STATUS_VARIANTS)[keyof typeof PAN_STATUS_VARIANTS];
  panResponse: IPanResponse | null;
}

export interface IValidatePANFailure {
  errorCode?: string;
  message: string;
}

const getPanFailureMessage = (errorCode?: string, fallbackMessage?: string) => {
  const normalizedErrorCode = String(errorCode || '').trim().toUpperCase();
  const defaultMessage = resolveKycErrorMessage(undefined, 'PAN');
  const resolvedMessage = normalizedErrorCode
    ? resolveKycErrorMessage(normalizedErrorCode, 'PAN')
    : '';

  if (normalizedErrorCode && resolvedMessage !== defaultMessage) {
    return resolvedMessage;
  }

  return String(fallbackMessage || resolvedMessage || defaultMessage).trim() || defaultMessage;
};

const createValidatePANFailure = (
  errorCode?: string,
  fallbackMessage?: string
): IValidatePANFailure => {
  const normalizedErrorCode = String(errorCode || '').trim().toUpperCase();

  return {
    errorCode: normalizedErrorCode || undefined,
    message: getPanFailureMessage(normalizedErrorCode, fallbackMessage),
  };
};

export const getValidatePANFailure = (value: unknown): IValidatePANFailure => {
  if (typeof value === 'string') {
    return createValidatePANFailure(undefined, value);
  }

  const candidate = value as {
    errorCode?: unknown;
    code?: unknown;
    message?: unknown;
    response?: {
      data?: {
        error?: unknown;
        message?: unknown;
      };
    };
  } | null;

  const responseData = candidate?.response?.data;
  const errorCode = String(responseData?.error || candidate?.errorCode || candidate?.code || '').trim().toUpperCase();
  const fallbackMessage = String(responseData?.message || candidate?.message || '').trim();

  return createValidatePANFailure(errorCode, fallbackMessage);
};

const defaultValues: IPANSlice = {
  loading: false,
  error: "",
  success: false,
  isPanInactive: false,
  isInitiallyValidated: false,
  isPanVerified: false,
  isPanUnavailable: false,
  panStatus: PAN_STATUS_VARIANTS.PENDING,
  fullName: "",
  panResponse: null,
};

// Async thunk for PAN validation
export const ValidatePAN = createAsyncThunk<
  IValidatePANResult,
  IValidatePAN,
  { rejectValue: IValidatePANFailure }
>(
  "validatePAN",
  async ({ data }: IValidatePAN, thunkAPI) => {
    const { dispatch } = thunkAPI;
    try {
      const appName:any = localStorage.getItem('appname');
      data['customerSource'] = AppSpecificName[appName];
      const response = await axiosInstance.post(
        getUrl(APIResource.validatePAN, [
          {
            variableName: "customerId",
            variableValue: data.customerId,
          },
        ]),
        {
          ...data,
        }
      );
      if (response.status === 200) {
        if (response.data && data.panNumber) {
          const responseData = response.data;
          if (Object.keys(responseData).length ===0){
             return thunkAPI.rejectWithValue(
              createValidatePANFailure()
            );
          }

          if (responseData?.message) {
            dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PENDING }));
            dispatch(setPanResponse({ value: null }));
            return thunkAPI.rejectWithValue(
              createValidatePANFailure(responseData?.error, responseData?.message)
            );
          } else if (!responseData.isCompliant) {
            dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_INACTIVE }));
            dispatch(setPanResponse({ value: responseData }));
            return {
              panStatus: PAN_STATUS_VARIANTS.PAN_INACTIVE,
              panResponse: responseData,
            } as IValidatePANResult;
          } else if (
            String(responseData?.panOperationalStatus).toUpperCase() ===
            "INOPERATIVE"
          ) {
            dispatch(
              setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_INOPERATIVE })
            );
            dispatch(setPanResponse({ value: responseData }));
            return {
              panStatus: PAN_STATUS_VARIANTS.PAN_INOPERATIVE,
              panResponse: responseData,
            } as IValidatePANResult;
          // } else if (responseData && responseData.associatedCustomerDataList && responseData.associatedCustomerDataList.length>0) {
          //   dispatch(
          //     setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_ASSOCIATION })
          //   );
          //   dispatch(setPanResponse({ value: responseData }));
          //   return {
          //     panStatus: PAN_STATUS_VARIANTS.PAN_ASSOCIATION,
          //     panResponse: responseData,
          //   } as IValidatePANResult;
          } else {
            dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_VERIFIED }));
            dispatch(setPanResponse({ value: responseData }));
            return {
              panStatus: PAN_STATUS_VARIANTS.PAN_VERIFIED,
              panResponse: responseData,
            } as IValidatePANResult;
          }
        } else {
          const panStatus = PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_CHECKED;
          dispatch(
            setPanStatus({
              value: panStatus,
            })
          );
          dispatch(setPanResponse({ value: null }));
          return {
            panStatus,
            panResponse: null,
          } as IValidatePANResult;
        }
      } else if (response.status === 400) {
        dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PENDING }));
        dispatch(setPanResponse({ value: null }));
        return thunkAPI.rejectWithValue(
         createValidatePANFailure("KYC-400")
        );
      } else {
        dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PENDING }));
        dispatch(setPanResponse({ value: null }));
        return thunkAPI.rejectWithValue(
          createValidatePANFailure()
        );
      }
    } catch (error: any) {
      dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PENDING }));
      dispatch(setPanResponse({ value: null }));
      return thunkAPI.rejectWithValue(
        createValidatePANFailure(
          error?.response?.data?.error || error?.code,
          error?.response?.data?.message || error?.message
        )
      );
    }
  }
);

// Slice definition
const PANSlice = createSlice({
  name: "validatePAN",
  initialState: defaultValues,
  reducers: {
    setPanStatus: (state, action) => {
      state.panStatus = action.payload.value;
    },
    setPanResponse: (state, action) => {
      state.panResponse = action.payload.value;
    },
    setSuccessValue: (state, action) => {
      state.success = action.payload.value;
    },
    setError: (state, action) => {
      state.error = action.payload.value;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ValidatePAN.pending, (state) => {
        state.loading = true;
        state.error = "";
        state.success = false;
      })
      .addCase(ValidatePAN.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(ValidatePAN.rejected, (state, action) => {
        state.loading = false;
        state.error = getValidatePANFailure(action.payload).message;
      });
  },
});

// Export actions and reducer
export const { setSuccessValue, setError, setPanStatus, setPanResponse } =
  PANSlice.actions;
export default PANSlice.reducer;
